import React, { useEffect, useRef, useState } from 'react';
import 'cesium/Build/Cesium/Widgets/widgets.css';
import * as Cesium from 'cesium';
import { MenuProps } from 'antd';
import CesiumNavigation from 'cesium-navigation-es6';
import { UserOutlined } from '@ant-design/icons';
import CustomFloatButton from '../../shared/components/FloatButton';
import CustomMapMenu from '../../shared/components/Menu';
import { MenuItemType } from 'antd/es/menu/interface';
import './CesiumMap.scss';
import { ApiRoutes } from '../../routes/routeConstants/apiRoutes';
import Spinner from '../../shared/components/Spinner';
import { useCesiumViewer } from '../../hooks/useCesiumViewer';

interface MenuItem extends MenuItemType {
  label: string;
  key: string;
  icon: React.ReactNode;
}

const items: MenuItem[] = [
  {
    label: '1st menu item',
    key: '1',
    icon: <UserOutlined />,
  },
  {
    label: '2nd menu item',
    key: '2',
    icon: <UserOutlined />,
  },
  {
    label: '3rd menu item',
    key: '3',
    icon: <UserOutlined />,
  },
  {
    label: '4th menu item',
    key: '4',
    icon: <UserOutlined />,
  },
];

interface CesiumMapProps {
  containerId: string;
}

const CesiumMap: React.FC<CesiumMapProps> = ({ containerId }) => {
  const [is3D, setIs3D] = useState<boolean>(false);
  // @ts-ignore comment
  const [dropDownText, setDropDownText] = useState<string>('Map View');
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const viewerRef = useRef<Cesium.Viewer | null>(null);
  const { isViewerReady } = useCesiumViewer({
    container: containerId,
    viewerRef,
    ionAccessToken: import.meta.env.VITE_APP_CESIUM_ACCESS_TOKEN as string,
    viewerOptions: {
      animation: false,
      timeline: false,
      shouldAnimate: true,
      fullscreenButton: false,
      infoBox: false,
      selectionIndicator: false,
      // sceneMode: Cesium.SceneMode.SCENE2D
    },
    recreateKey: `${String(containerId)}-${String(is3D)}`,
  });

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    const selectedItem = items.find((i) => i.key === e.key);

    if (selectedItem) {
      setDropDownText(selectedItem?.label);
    }
  };

  // @ts-ignore comment
  const menuProps: MenuProps = {
    items,
    onClick: handleMenuClick,
  };

  useEffect(() => {
    if (!isViewerReady || !viewerRef.current) return;

    const viewer = viewerRef.current;
    const options = {
      orientation: {
        heading: Cesium.Math.toRadians(1350.94452087411315),
        pitch: Cesium.Math.toRadians(-66.6402342251215),
        roll: Cesium.Math.toRadians(360),
      },
      duration: 10,
      enableCompass: true,
      enableZoomControls: true,
      enableDistanceLegend: true,
      enableCompassOuterRing: true,
      resetTooltip: 'Reset',
      zoomInTooltip: 'ZoomIn',
      zoomOutTooltip: 'ZoomOut',
    };
    new CesiumNavigation(viewer, options);

    if (is3D) {
      const loadCesiumIonAsset = async () => {
        try {
          const tileset = await Cesium.Cesium3DTileset.fromIonAssetId(2498601);
          const extras = tileset.asset.extras;
          viewerRef.current?.scene.primitives.add(tileset);
          viewerRef.current?.zoomTo(tileset);
          if (Cesium.defined(extras) && Cesium.defined(extras.ion) && Cesium.defined(extras.ion.defaultStyle)) {
            tileset.style = new Cesium.Cesium3DTileStyle(extras.ion.defaultStyle);
          }
        } catch (error) {
          console.error(`Failed to load tileset: ${error}`);
        }
      };

      const addBuildingTileset = async () => {
        try {
          const tileset = await Cesium.createGooglePhotorealistic3DTileset();
          viewerRef.current?.scene.primitives.add(tileset);
        } catch (error) {
          console.error(`Failed to load tileset: ${error}`);
        }
      };

      Promise.all([loadCesiumIonAsset(), addBuildingTileset()]);
    } else {
      if (viewerRef.current) {
        viewerRef.current.scene.globe.depthTestAgainstTerrain = true;
        viewerRef.current.scene.mode = Cesium.SceneMode.SCENE2D;

        const fetchLayersFromGeoServer = async (): Promise<string[]> => {
          const username = import.meta.env.VITE_APP_CESIUM_USERNAME as string;
          const password = import.meta.env.VITE_APP_CESIUM_PASSWORD as string;
          const credentials = btoa(`${username}:${password}`);

          try {
            const response = await fetch(`${ApiRoutes.GEOSERVER_BASE_URL}/geoserver/rest/workspaces/cesium-viewer/layers.json`, {
              headers: {
                Authorization: `Basic ${credentials}`,
                'Content-Type': 'application/json',
              },
            });

            if (!response.ok) {
              throw new Error(`Failed to fetch layers: ${response.statusText}`);
            }

            const data = await response.json();
            const layers = data.layers.layer.map((layer: any) => layer.name);
            return layers;
          } catch (error) {
            console.error('Error fetching layers from GeoServer:', error);
            return [];
          }
        };

        const loadWMSLayer = async () => {
          setIsLoading(true);
          const layers = await fetchLayersFromGeoServer();

          if (layers.length > 0) {
            const imageryProvider = await new Cesium.WebMapServiceImageryProvider({
              url: `${ApiRoutes.GEOSERVER_BASE_URL}/geoserver/cesium-viewer/wms`,
              layers: layers.join(','),
              parameters: {
                transparent: true,
                format: 'image/png',
              },
            });

            const imageryLayer = await new Cesium.ImageryLayer(imageryProvider);
            await viewerRef.current?.scene.imageryLayers.add(imageryLayer);

            await viewerRef.current?.scene.camera.setView({
              destination: Cesium.Cartesian3.fromDegrees(-93.3818642667026, 48.70855153441889, 2000),
            });
          }
          setIsLoading(false);
        };
        loadWMSLayer();
      }
    }
    console.log(isLoading);
  }, [isViewerReady, is3D, containerId]);

  return (
    <>
      <Spinner isLoading={isLoading} />
      <div id={containerId} className="cesium-viewer">
        <CustomMapMenu />
        <CustomFloatButton is3D={is3D} setIs3d={() => setIs3D(!is3D)} />
      </div>
    </>
  );
};

export default CesiumMap;
